import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { Company } from '../types/models/company/company.model';
import { TaskType } from '../types/models/task/task-type.model';
import { ActiveCompanyService } from './active-company.service';
import { DataService } from './data-service';
import { TaskTypeDTO } from './dtos/project-tasks/project-task-type.dt';
import { TaskTypeFactory } from './factories/task-type.factory';

/**
 * TaskTypesDataService - Task types per company: store and lazy fetch by company id.
 *
 * Resolves the active company from {@link ActiveCompanyService.activeCompany} for `getActiveCompanyTaskTypes()`.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Task creation, filters, and task-type pickers scoped to the active company.
 *
 * Architecture:
 * - {@link TaskTypesDataService}: Types store and HTTP
 * - {@link ActiveCompanyService}: Current company for default task type list
 */
@Injectable({ providedIn: 'root' })
export class TaskTypesDataService extends DataService<TaskTypeDTO, TaskType> {
  private readonly taskTypes: DataStore<TaskType> = new DataStore<TaskType>();
  private readonly taskTypesForCompany: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly taskTypeFactory: TaskTypeFactory = inject(TaskTypeFactory);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);

  private readonly activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  public getById(taskTypeId: string): Signal<TaskType | undefined> {
    return computed(() => this.taskTypes.get(taskTypeId)());
  }

  public getActiveCompanyTaskTypes(): Signal<TaskType[] | undefined> {
    return computed(() => {
      const company = this.activeCompany();
      if (!company) return [];

      const companyId = company.id;

      if (!this.taskTypesForCompany.hasDataForId(companyId)) {
        this.fetchCompanyTaskTypes(companyId);
      }

      return this.getCompanyTaskTypes(companyId)();
    });
  }

  public getCompanyTaskTypes(companyId: string): Signal<TaskType[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.taskTypesForCompany
          .get(companyId)()
          ?.ids?.map(id => this.taskTypes.get(id)()) || []
    );
  }

  public loadTaskType(taskTypeId: string): void {
    if (this.taskTypes.hasDataForId(taskTypeId)) {
      return;
    }
    this.fetchTaskType(taskTypeId);
  }

  public upsertLocalData(dto: TaskTypeDTO): Signal<TaskType> {
    return dto ? this.taskTypes.upsert(this.taskTypeFactory.produce(dto)) : signal(undefined);
  }

  protected fetchTaskType(taskTypeId: string): void {
    this.httpClient.get<TaskTypeDTO>(environment.APIEndPoint + `task-types/${taskTypeId}`).subscribe({
      next: (dto: TaskTypeDTO) => {
        this.taskTypes.upsert(this.taskTypeFactory.produce(dto));
      }
    });
  }

  protected fetchCompanyTaskTypes(companyId: string): void {
    this.httpClient.get<TaskTypeDTO[]>(environment.APIEndPoint + `companies/${companyId}/task-types`).subscribe({
      next: (taskTypes: TaskTypeDTO[]) => {
        const ids = taskTypes.map(dto => dto.id);
        this.taskTypesForCompany.upsert({
          id: companyId,
          ids: ids
        });

        if (taskTypes?.length) {
          const produced = taskTypes.map(dto => this.taskTypeFactory.produce(dto));
          this.taskTypes.upsertMany(produced);
        }
      }
    });
  }
}
